import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  CONTACT_SETTINGS_API,
  ContactSettings,
  defaultContactSettings,
  isContactSettings,
} from "./contactSettings";

interface ContactSettingsContextValue {
  settings: ContactSettings;
  reload: () => Promise<void>;
}

const ContactSettingsContext = createContext<ContactSettingsContextValue>({
  settings: defaultContactSettings,
  reload: async () => undefined,
});

export const ContactSettingsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [settings, setSettings] = useState(defaultContactSettings);

  const reload = useCallback(async () => {
    try {
      const response = await fetch(`${CONTACT_SETTINGS_API}/public/contact-settings`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;
      const body: unknown = await response.json();
      if (body && typeof body === "object" && "settings" in body) {
        const published = (body as { settings: unknown }).settings;
        if (isContactSettings(published)) setSettings(published);
      }
    } catch {
      // The bundled data keeps the public site usable if the service is unavailable.
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <ContactSettingsContext.Provider value={{ settings, reload }}>
      {children}
    </ContactSettingsContext.Provider>
  );
};

export const useContactSettings = () => useContext(ContactSettingsContext);
